import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import {
  DEFAULT_KDF_PARAMS,
  deriveKek,
  fromBase64,
  generateMasterKey,
  generateSalt,
  toBase64,
  unwrapKey,
  wrapKey,
} from '../crypto/backupCrypto';
import {
  createEncryptionKey,
  EncryptionKeyRecord,
  getEncryptionKey,
  rotateEncryptionKey,
} from '../api/encryptionKeyApi';

const MASTER_KEY_SESSION_STORAGE_KEY = 'celepus_backup_master_key';

interface EncryptionContextValue {
  loading: boolean;
  needsSetup: boolean; // no encryption key exists on the server yet
  locked: boolean; // a key exists but this session hasn't unwrapped it yet
  masterKey: CryptoKey | null;
  setup: (passphrase: string) => Promise<void>;
  unlock: (passphrase: string) => Promise<void>;
  rotate: (currentPassphrase: string, newPassphrase: string) => Promise<void>;
}

const EncryptionContext = createContext<EncryptionContextValue | undefined>(undefined);

const importRawMasterKey = (raw: Uint8Array): Promise<CryptoKey> =>
  crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);

interface OwnProps {
  children?: ReactNode;
}

const EncryptionProvider = (props: OwnProps) => {
  const { children } = props;
  const [keyRecord, setKeyRecord] = useState<EncryptionKeyRecord | null | undefined>(undefined);
  const [masterKey, setMasterKey] = useState<CryptoKey | null>(null);

  useEffect(() => {
    getEncryptionKey().then(setKeyRecord);
  }, []);

  useEffect(() => {
    if (!keyRecord || masterKey) {
      return;
    }
    // A key exists server-side — see if this browser session already
    // unlocked it (survives page refreshes within the same tab session,
    // cleared on browser/tab close, never written to localStorage).
    const cached = sessionStorage.getItem(MASTER_KEY_SESSION_STORAGE_KEY);
    if (!cached) {
      return;
    }
    importRawMasterKey(fromBase64(cached))
      .then(setMasterKey)
      .catch(() => sessionStorage.removeItem(MASTER_KEY_SESSION_STORAGE_KEY));
  }, [keyRecord, masterKey]);

  const persistMasterKey = async (mk: CryptoKey) => {
    const raw = await crypto.subtle.exportKey('raw', mk);
    sessionStorage.setItem(MASTER_KEY_SESSION_STORAGE_KEY, toBase64(new Uint8Array(raw)));
    setMasterKey(mk);
  };

  const setup = async (passphrase: string) => {
    const salt = generateSalt();
    const kek = await deriveKek(passphrase, salt, DEFAULT_KDF_PARAMS);
    const mk = await generateMasterKey();
    const wrapped = await wrapKey(mk, kek);

    const record = await createEncryptionKey({
      wrapped_master_key: wrapped.wrapped,
      wrap_nonce: wrapped.nonce,
      salt: toBase64(salt),
      kdf_params: DEFAULT_KDF_PARAMS,
    });

    setKeyRecord(record);
    await persistMasterKey(mk);
  };

  const unlock = async (passphrase: string) => {
    if (!keyRecord) {
      throw new Error('No backup encryption key to unlock.');
    }

    const kek = await deriveKek(passphrase, fromBase64(keyRecord.salt), keyRecord.kdf_params);
    // A wrong passphrase derives a wrong KEK — AES-GCM's auth tag check
    // inside unwrapKey rejects it with a DOMException rather than silently
    // returning garbage, so this naturally surfaces as a thrown error.
    const mk = await unwrapKey({ wrapped: keyRecord.wrapped_master_key, nonce: keyRecord.wrap_nonce }, kek);
    await persistMasterKey(mk);
  };

  const rotate = async (currentPassphrase: string, newPassphrase: string) => {
    if (!keyRecord) {
      throw new Error('No backup encryption key to rotate.');
    }

    // The master key itself never changes on rotation — only re-derive it
    // from scratch if this session hasn't already unwrapped it.
    let mk = masterKey;
    if (!mk) {
      const currentKek = await deriveKek(currentPassphrase, fromBase64(keyRecord.salt), keyRecord.kdf_params);
      mk = await unwrapKey({ wrapped: keyRecord.wrapped_master_key, nonce: keyRecord.wrap_nonce }, currentKek);
    }

    const newSalt = generateSalt();
    const newKek = await deriveKek(newPassphrase, newSalt, DEFAULT_KDF_PARAMS);
    const wrapped = await wrapKey(mk, newKek);

    const record = await rotateEncryptionKey({
      wrapped_master_key: wrapped.wrapped,
      wrap_nonce: wrapped.nonce,
      salt: toBase64(newSalt),
      kdf_params: DEFAULT_KDF_PARAMS,
    });

    setKeyRecord(record);
    await persistMasterKey(mk);
  };

  return (
    <EncryptionContext.Provider
      value={{
        loading: keyRecord === undefined,
        needsSetup: keyRecord === null,
        locked: !!keyRecord && !masterKey,
        masterKey,
        setup,
        unlock,
        rotate,
      }}
    >
      {children}
    </EncryptionContext.Provider>
  );
};

const useEncryption = (): EncryptionContextValue => {
  const context = useContext(EncryptionContext);
  if (!context) {
    throw new Error('useEncryption must be used within an EncryptionProvider');
  }
  return context;
};

export {
  EncryptionProvider,
  useEncryption,
};
